import React from 'react';

// Design System Components
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Module Components
import SectionHeading from './SectionHeading';

// Constants
import { STRINGS } from '../../../constants';

// Types
import { CardOrientation } from '../../../types';

interface CardPropertiesPanelProps {
  orientation: CardOrientation;
  footerEnabled: boolean;
  onOrientationChange: (orientation: CardOrientation) => void;
  onFooterChange: (enabled: boolean) => void;
}

/**
 * The inspector with **nothing selected**: the card's own properties.
 *
 * WHY THESE LIVE HERE
 * -------------------
 * Card orientation and "Add a footer" used to sit at the top of the configuration
 * pane, above the field list. Two problems with that:
 *
 *  1. They cost ~190px of the one column that has 29 rows to show. On a laptop the
 *     field list opened already scrolled, with the first fields cut off.
 *  2. They are *card* properties sitting in the *field catalogue*. The pane answers
 *     "which fields are on this card"; orientation answers "what shape is this
 *     card". Different questions.
 *
 * The inspector already answers "properties of the thing you have selected". With
 * nothing selected, the thing is the card — which is the operator's own UX contract
 * (PRODUCT-CARD-INPUTS.md §2.4: *"With nothing selected it shows the card's own
 * properties (orientation, footer, launch-on, stock labels)"*), and the pattern
 * every comparable builder uses.
 *
 * The deciding argument was scale: §2.4 names **four** card properties. Two more —
 * launch-on and stock labels — are coming. The pane top could not hold four, and
 * neither could the header; a panel can.
 *
 * NOTE ON THE SPEC
 * ----------------
 * This supersedes R32's and R59's placement wording, both of which pin these two
 * controls to the pane on the operator's own earlier instruction ("never swapped or
 * displaced", 23:26). The operator re-decided on 2026-08-15 in favour of §2.4. The
 * *behaviour* R32 pins — exactly one of Vertical/Horizontal, radios stacked
 * vertically — is unchanged and still asserted.
 *
 * Mutual exclusivity comes from the single `orientation` value rather than two
 * independent booleans, so "both" and "neither" stay unrepresentable.
 */
const CardPropertiesPanel = ({
  orientation,
  footerEnabled,
  onOrientationChange,
  onFooterChange,
}: CardPropertiesPanelProps) => (
  <section
    aria-label={STRINGS.pane.cardPropertiesTitle}
    className="tw-w-80 tw-h-full tw-shrink-0 tw-self-stretch tw-bg-surface tw-border-l tw-border-edge"
  >
    {/* Same shell as PropertiesPanel: the column's own scroll container, so the two
        inspector states are one column that changes contents rather than two columns
        that take turns (spec R63, R74). */}
    <div className="md:tw-h-full md:tw-overflow-y-auto">
      <header className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-bg-surface tw-border-b tw-border-edge">
        <Typography variant="title-header" as="h3" className="tw-text-ink">
          {STRINGS.pane.cardPropertiesTitle}
        </Typography>
      </header>

      <section className="tw-flex tw-flex-col tw-border-b tw-border-edge">
        <div className="tw-px-3 tw-py-2 tw-bg-surface-sunken tw-border-b tw-border-edge">
          <SectionHeading>{STRINGS.pane.orientationTitle}</SectionHeading>
        </div>

        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-2">
          <div
            role="radiogroup"
            aria-label={STRINGS.pane.orientationTitle}
            className="tw-flex tw-flex-col tw-gap-2"
          >
            <Radio
              name="product-card-orientation"
              value="vertical"
              label={STRINGS.pane.orientationVertical}
              checked={orientation === 'vertical'}
              onChange={() => onOrientationChange('vertical')}
            />
            <Radio
              name="product-card-orientation"
              value="horizontal"
              label={STRINGS.pane.orientationHorizontal}
              checked={orientation === 'horizontal'}
              onChange={() => onOrientationChange('horizontal')}
            />
          </div>

          <div className="tw-pt-2 tw-border-t tw-border-edge">
            <Checkbox
              label={STRINGS.pane.footerCheckbox}
              checked={footerEnabled}
              onChange={onFooterChange}
              size="sm"
            />
          </div>
        </div>
      </section>

      {/* Says what the empty half of the inspector is for. Without it the panel
          reads as "these are all the configuration there are", and the user has no cue
          that selecting a block puts more here. */}
      <p className="tw-px-4 tw-py-3">
        <Typography variant="caption-metadata" as="span" className="tw-text-ink-tertiary">
          {STRINGS.pane.cardPropertiesHint}
        </Typography>
      </p>
    </div>
  </section>
);

export default CardPropertiesPanel;
